/* eslint-disable import/order */
import {
    PaidNewslettersActionTypes,
    DeletePaidNewsletterAction,
    GetAllPaidNewslettersStartAction,
    LoadPreviewTemplateAction,
    UpdatePaidNewsletterStatusStartAction,
    UrlSearchParams,
} from '@/store/paidNewsletters/types';
import {
    DeletePaidNewsletterError,
    DeletePaidNewsletterSuccess,
    GetAllPaidNewslettersError,
    GetAllPaidNewslettersStart,
    GetAllPaidNewslettersSuccess,
    LoadPreviewTemplateError,
    LoadPreviewTemplateSuccess,
    PickPaidNewsletters,
    UpdatePaidNewsletterStatusError,
    UpdatePaidNewsletterStatusSuccess,
} from '@/store/paidNewsletters/actions';
import { call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { deletePaidNewsletters, loadPaidNewsletter, loadPaidNewsletters, updatePaidNewsletterStatus } from '@/services/ElasticApi';
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { NewsletterStatusEnum } from '@dev/ee-api/ee-api-models';
import { calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';
import i18n from '@/../i18n';
import { infoToast } from './commonGenerators/infoToast';
import { push } from 'redux-first-history';
import { stringify } from 'query-string';

function* fetchTemplatesList(action: GetAllPaidNewslettersStartAction): any {
    const { payload } = action;
    const offset = calculateOffset(payload.page, payload.limit);

    const params = {
        offset,
        limit: payload.limit,
        search: payload.search,
        statusList: payload.statusList,
        page: payload.page,
        from: payload.from,
        to: payload.to,
        scope: payload.scope
    };

    const activePage = calculateActivePage(offset, payload.limit);

    try {
        const list = yield call(loadPaidNewsletters, params);

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: payload.limit,
            ...(payload.statusList && { statuses: payload.statusList }),
            ...(payload.search && { search: payload.search }),
            ...(payload.from && { from: payload.from }),
            ...(payload.to && { to: payload.to }),
        };
        const counts = {
            [NewsletterStatusEnum.Published]: Number(list.headers['x-total-published-newsletters']),
            [NewsletterStatusEnum.Unpublished]: Number(list.headers['x-total-unpublished-newsletters']),
            [NewsletterStatusEnum.Draft]: Number(list.headers['x-total-draft-newsletters'])
        }

        const paidNewsletterTabCount = payload.statusList.reduce((sum, status) => sum + (counts[status] || 0), 0);
        if (!payload.isUrlUpdateDisabled) {
            yield put(push({ search: stringify(urlQueryParams as any) }));
        }
        yield put(GetAllPaidNewslettersSuccess(list.data, paidNewsletterTabCount, counts[NewsletterStatusEnum.Published], counts[NewsletterStatusEnum.Unpublished]));
    } catch (e) {
        yield call(errorToast, e);
        yield put(GetAllPaidNewslettersError());
    }
}

function* previewPaidNewsletter(action: LoadPreviewTemplateAction): any {
    const { payload } = action;

    try {
        const paidNewsletter = yield call(loadPaidNewsletter, payload.name);
        yield put(LoadPreviewTemplateSuccess(paidNewsletter));
    } catch (e) {
        yield call(errorToast, e);
        yield put(LoadPreviewTemplateError());
    }
}

function* updatePaidNewsletterStatusByName(action: UpdatePaidNewsletterStatusStartAction): any {
    const { payload } = action;
    try {
        const paidNewsletterStatus = yield call(updatePaidNewsletterStatus, payload.name, payload.status);

        yield put(UpdatePaidNewsletterStatusSuccess(paidNewsletterStatus, payload.name));

        yield call(infoToast, payload.status === NewsletterStatusEnum.Published ? i18n.t('paidnewsletterpublished') : i18n.t('paidnewsletterunpublished'));
        
    } catch (e) {
        yield call(errorToast, e);
        yield put(UpdatePaidNewsletterStatusError(payload.name));
    }
}

function* fetchDeletePaidNewsletters(action: DeletePaidNewsletterAction): any {
    const { payload } = action;

    try {
        const offset = calculateOffset(payload.UrlParams.page, payload.UrlParams.limit);
        const params = {
            offset,
            limit: payload.UrlParams.limit,
            search: payload.UrlParams.search,
            statusList: payload.UrlParams.statusList,
            page: payload.UrlParams.page,
        };
        yield call(deletePaidNewsletters, payload.Names);
        yield put(DeletePaidNewsletterSuccess());
        yield put(GetAllPaidNewslettersStart(params));
        yield put(PickPaidNewsletters([]));
        yield call(infoToast, i18n.t('paidnewsletterdeleted', { count: payload.Names.length }));
    } catch (e) {
        yield call(errorToast, e);
        yield put(DeletePaidNewsletterError());
    }
}

function* watchFetchTemplateList(): any {
    yield takeLatest(PaidNewslettersActionTypes.GET_ALL_PAID_NEWSLETTERS_START, fetchTemplatesList);
}
function* watchPreviewPaidNewsletter(): any {
    yield takeLatest(PaidNewslettersActionTypes.LOAD_PREVIEW_TEMPLATE, previewPaidNewsletter);
}
function* watchUpdatePaidNewsletterStatus(): any {
    yield takeEvery(PaidNewslettersActionTypes.UPDATE_PAID_NEWSLETTER_STATUS, updatePaidNewsletterStatusByName);
}
function* watchDeletePaidNewsletters(): any {
    yield takeLatest(PaidNewslettersActionTypes.DELETE_PAID_NEWSLETTERS, fetchDeletePaidNewsletters);
}

const paidNewslettersListSagas = [
    fork(watchFetchTemplateList),
    fork(watchDeletePaidNewsletters),
    fork(watchPreviewPaidNewsletter),
    fork(watchUpdatePaidNewsletterStatus),
];

export default paidNewslettersListSagas;
